import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  HeatMaterialWithColor,
  MixDisplayMode,
  ProcessedNonBasketSource,
  mixValueModeFor,
  mixViewFor,
} from "../types";
import { HeatMixPlot } from "./HeatMixPlot";
import { unitForMixView } from "./mixViewUnit";

interface HeatNonBasketSourceCardProps {
  source: ProcessedNonBasketSource;
  isFirst: boolean;
  displayMode: MixDisplayMode;
}

export const HeatNonBasketSourceCard: React.FC<
  HeatNonBasketSourceCardProps
> = ({ source, isFirst, displayMode }) => {
  const { t } = useTranslation();

  const mixView = mixViewFor(displayMode);
  const mixValueMode = mixValueModeFor(displayMode);
  const isPercent = mixValueMode === "mass_fraction";
  const unit = unitForMixView(mixView);
  const total = source.materials.reduce((sum, m) => sum + m.value, 0);
  const referenceTotal = source.materials.reduce(
    (sum, m) => sum + m.value - (m.diff ?? 0),
    0
  );
  const materials: HeatMaterialWithColor[] = source.materials.map((m) => {
    const value = isPercent && total > 0 ? (100 * m.value) / total : m.value;
    const refValue = m.value - (m.diff ?? 0);
    const pctDiff =
      m.diff !== null && total > 0 && referenceTotal > 0
        ? (100 * m.value) / total - (100 * refValue) / referenceTotal
        : 0;
    return {
      name: m.name,
      value,
      unit,
      diffFromPreviousStage: isPercent ? pctDiff : (m.diff ?? 0),
      ignored: !m.isPresent,
      highlighted: false,
      color: m.color,
    };
  });

  return (
    <Stack direction="column" alignItems="center" width={244} gap={1}>
      <Typography variant="h5" textAlign="center">
        {t(source.sourceName)}
      </Typography>
      <HeatMixPlot
        materials={materials}
        isFirst={isFirst}
        mixValueMode={mixValueMode}
      />
    </Stack>
  );
};
